import { parse, useForm } from '@conform-to/react';
import { getZodConstraint, parseWithZod } from '@conform-to/zod';
import type { ZodTypeAny, input } from 'zod';

type UseFormOptions<Schema extends ZodTypeAny> = Parameters<typeof useForm<input<Schema>>>[0];

export interface UseZodFormOptions<Schema extends ZodTypeAny>
  extends Omit<UseFormOptions<Schema>, 'onValidate' | 'constraint'> {
  /**
   * The schema used for client-side validation. Pass a function when the schema depends on submitted values,
   * e.g. a field that is only required for a given `type`. Omit it for forms that only collect values.
   */
  schema?: Schema | ((formData: FormData) => Schema);
  /** The schema used to derive native constraints (`required`, `minLength`, ...). Defaults to `schema` when static. */
  constraintSchema?: Schema;
}

/**
 * Conform `useForm` with the project defaults: Zod validation on blur, revalidation on input.
 * Conform owns the form state (`form.value`, `form.dirty`, `form.update`, `form.reset`), so there is no
 * parallel React state to keep in sync.
 */
export function useZodForm<Schema extends ZodTypeAny>({
  schema,
  constraintSchema,
  ...options
}: UseZodFormOptions<Schema>) {
  const staticSchema = constraintSchema ?? (typeof schema === 'function' ? undefined : schema);

  return useForm<input<Schema>>({
    shouldValidate: 'onBlur',
    shouldRevalidate: 'onInput',
    constraint: staticSchema ? getZodConstraint(staticSchema) : undefined,
    // Note: without `onValidate`, Conform lets intents such as `form.update`/`form.reset` submit the form natively,
    // so forms without a schema still parse (without validating) to keep intents on the client
    onValidate: ({ formData }) =>
      schema
        ? parseWithZod(formData, { schema: typeof schema === 'function' ? schema(formData) : schema })
        : parse(formData, { resolve: (value) => ({ value }) }),
    ...options,
  });
}
